import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSliderModule } from '@angular/material/slider';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxJsonViewerModule } from 'ngx-json-viewer';
import { Component, Inject } from '@angular/core';
import { Organization, SecurityTask, TaskPresentation, toTaskPresentation } from '@newgenus/common';
import { DatePipe, NgClass } from '@angular/common';

interface ChangedField {
    /** Top-level key, e.g. `name`, `colour`, `vfp`. */
    key: string;
    /** Stringified previous value (truncated for display). */
    before: string;
    /** Stringified new value (truncated for display). */
    after: string;
    /** Whether both values are short enough to render inline. */
    isShort: boolean;
    /** When true, this represents a removed field (delete operation) and only `before` is rendered. */
    isRemoval?: boolean;
}

@Component({
    selector: 'shared-org-notification-dialog',
    imports: [
        MatDialogModule,
        MatButtonModule,
        NgClass,
        DatePipe,
        MatIconModule,
        MatTabsModule,
        NgxJsonViewerModule,
        ReactiveFormsModule,
        FormsModule,
        MatSliderModule,
        MatSlideToggleModule,
    ],
    templateUrl: './org-notification-dialog.html',
    styleUrls: ['./org-notification-dialog.scss', '../org-dialog.scss', '../../organization-board.component.scss'],
})
export class OrgNotificationDialogComponent {
    public depth = 2;
    public allowOverflow = false;

    public readonly task: SecurityTask | null;
    public readonly organization: Organization | null;
    public readonly isDeveloper: boolean;
    public readonly presentation: TaskPresentation | null;
    public readonly actorName: string;
    public readonly actorRole: 'modified' | 'created';
    public readonly changedFields: ChangedField[];
    public readonly isDelete: boolean;

    constructor(
        public dialogRef: MatDialogRef<OrgNotificationDialogComponent>,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            organization: Organization;
            task?: SecurityTask;
            isDeveloper?: boolean;
        }
    ) {
        this.task = data?.task ?? null;
        this.organization = data?.organization ?? null;
        this.isDeveloper = data?.isDeveloper ?? false;

        this.presentation = this.task
            ? toTaskPresentation(this.task, {
                  orgParts: this.organization?.orgParts as any,
                  orgHats: this.organization?.orgHats as any,
              })
            : null;

        const { name, role } = this.resolveActor(this.task, this.organization);
        this.actorName = name;
        this.actorRole = role;

        this.isDelete = this.isDeleteTask(this.task);
        this.changedFields = this.task ? this.computeChangedFields(this.task, this.isDelete) : [];
    }

    public get statusClass(): string {
        return (this.presentation?.statusLabel || '').toLowerCase();
    }

    public get hasChanges(): boolean {
        return this.changedFields.length > 0;
    }

    private resolveActor(
        task: SecurityTask | null,
        org: Organization | null
    ): { name: string; role: 'modified' | 'created' } {
        if (!task) {
            return { name: 'Unknown user', role: 'modified' };
        }
        const blame = task.blameDetails;
        const role: 'modified' | 'created' = blame?.modifiedBy ? 'modified' : 'created';
        const uid = blame?.modifiedBy || blame?.createdBy || '';
        if (!uid) {
            return { name: 'Unknown user', role };
        }
        const orgUsers = (org?.orgUsers as any) || {};
        const user = orgUsers[uid];
        const name = user?.name || user?.userName || user?.email || uid;
        return { name, role };
    }

    private computeChangedFields(task: SecurityTask, isDelete: boolean): ChangedField[] {
        // For delete operations the diff isn't meaningful — there is no "new"
        // state. Render the snapshotOld fields as a "deleted" list instead so
        // the user sees WHAT was removed, not a fake before/after.
        if (isDelete) {
            const removed = task.snapshotOld || (task as any).diffOld || {};
            const skip = new Set([
                'key',
                'orgId',
                'createdBy',
                'createdOn',
                'modifiedBy',
                'modifiedOn',
                'updateTimeStamp',
                'createdTimeStamp',
                'completedTimestamp',
                '_id',
                'id',
                'taskNumber',
                'run',
            ]);
            const out: ChangedField[] = [];
            Object.keys(removed).forEach((k) => {
                if (skip.has(k)) {
                    return;
                }
                const v = removed[k];
                if (v === undefined || v === null || v === '') {
                    return;
                }
                const beforeStr = this.formatValue(v);
                out.push({
                    key: k,
                    before: beforeStr,
                    after: '',
                    isShort: beforeStr.length <= 60,
                    isRemoval: true,
                });
            });
            return out;
        }

        // Prefer diff* if present (these are the smaller "what actually changed"
        // payloads); fall back to comparing full snapshots.
        const diffNew = (task as any).diffNew;
        const diffOld = (task as any).diffOld;
        const newObj = diffNew && Object.keys(diffNew).length ? diffNew : task.snapshotNew;
        const oldObj = diffOld && Object.keys(diffOld || {}).length ? diffOld : task.snapshotOld;

        if (!newObj && !oldObj) {
            return [];
        }

        const keys = new Set<string>([...Object.keys(newObj || {}), ...Object.keys(oldObj || {})]);

        // Skip noisy / internal / audit keys — handled elsewhere in the summary.
        const skip = new Set([
            'key',
            'orgId',
            'createdBy',
            'createdOn',
            'modifiedBy',
            'modifiedOn',
            'updateTimeStamp',
            'createdTimeStamp',
            'completedTimestamp',
            '_id',
            'id',
            'taskNumber',
            'run',
        ]);

        const fields: ChangedField[] = [];
        keys.forEach((k) => {
            if (skip.has(k)) {
                return;
            }
            const before = (oldObj || {})[k];
            const after = (newObj || {})[k];
            if (this.isEqual(before, after)) {
                return;
            }
            const beforeStr = this.formatValue(before);
            const afterStr = this.formatValue(after);
            fields.push({
                key: k,
                before: beforeStr,
                after: afterStr,
                isShort: beforeStr.length <= 60 && afterStr.length <= 60,
            });
        });

        return fields;
    }

    private isDeleteTask(task: SecurityTask | null): boolean {
        if (!task) {
            return false;
        }
        const t = task.typeOfTask as string;
        return (
            t === 'removePart' ||
            t === 'removeHat' ||
            t === 'removeUser' ||
            t === 'removeSecurityGroup' ||
            t === 'removeFeature' ||
            t === 'removeFeaturePermissions' ||
            t === 'removePolicy' ||
            t === 'bulkDeleteParts'
        );
    }

    private isEqual(a: unknown, b: unknown): boolean {
        if (a === b) {
            return true;
        }
        if (a === null || b === null || a === undefined || b === undefined) {
            return a === b;
        }
        if (typeof a !== 'object' || typeof b !== 'object') {
            return false;
        }
        // Order-insensitive deep compare: stringify with sorted keys.
        try {
            return this.canonicalStringify(a) === this.canonicalStringify(b);
        } catch {
            return false;
        }
    }

    private canonicalStringify(v: unknown): string {
        return JSON.stringify(v, (_k, val) => {
            if (val && typeof val === 'object' && !Array.isArray(val)) {
                const sorted: Record<string, unknown> = {};
                Object.keys(val)
                    .sort()
                    .forEach((k) => {
                        sorted[k] = (val as Record<string, unknown>)[k];
                    });
                return sorted;
            }
            return val;
        });
    }

    private formatValue(v: unknown): string {
        if (v === undefined || v === null) {
            return '—';
        }
        if (typeof v === 'string') {
            return v.length > 200 ? v.substring(0, 200) + '…' : v;
        }
        if (typeof v === 'number' || typeof v === 'boolean') {
            return String(v);
        }
        try {
            const json = JSON.stringify(v);
            return json.length > 200 ? json.substring(0, 200) + '…' : json;
        } catch {
            return String(v);
        }
    }
}
